import {
  type CheckoutBasket,
  PAYMENT_TYPES,
  type CustomerDetails,
  type LoyaltyUser,
  type PaymentDetails,
  type StoredValueRequirement,
  type SubmitBasketData,
} from '@koala/sdk';
import { type IFrameChangeEvent } from '@/components/checkout/payment/pci-proxy/form/types';
import { InitializationErrorTypes, INITIALIZATION_ERRORS } from '@/constants/basket';
import { API_FOR_CONVEYANCE_TYPES_SWAP, DEFAULT_GUEST_USER } from '@/constants/global';
import { type RootState } from '@/types/app';
import { type IUserInfo } from '@/types/checkout';
import { type IBasketFulfillment, type IConveyanceDeliveryAddress } from '@/types/fulfillment';
import { type IStoredValue } from '@/types/paymentInfo';
import { invalidPhone, isMaskedEmail } from '@/utils/validation';
import { sanitizePhoneNumber } from '@/utils/phoneNumber';
import { DELIVERY_HANDOFF_TYPE_DEFAULT } from '@/constants/conveyance';

export const isGuestOrUser = (guestUser: IUserInfo | null, me: LoyaltyUser | null) =>
  !!guestUser || !!me?.id;

export const getIdsFromState = (state: RootState) => {
  const locationId = state.app.basket.checkoutBasket?.location?.id;
  const basketId = state.app.basket.checkoutBasket?.id;

  return {
    basketId,
    locationId,
  };
};

export const getLocationFromState = (state: RootState) => {
  return state.app.basket?.location;
};

export const normalizeExpiry = (value: string, previousValue: string) => {
  if (value) {
    let expires = value.replace(/\D/g, '').substr(0, 4);
    const length = value.length;
    // Allow the user to type in the /
    if (length === 3 && value.slice(2) === '/') {
      return value;
    }
    // User can delete the /
    if (previousValue && length === 2 && previousValue.slice(2) === '/') {
      return expires;
    }
    // Or just add / for them
    if (expires.length > 1) {
      expires = expires.substr(0, 2) + '/' + expires.substr(2);
    }
    // return a single digit
    return expires;
  }

  return '';
};

export const formatExpiry = (expiry: string) => {
  const expParts = expiry ? expiry.split('/') : [];

  return {
    expiry_month: parseInt(expParts[0], 0),
    expiry_year: parseInt(`20${expParts[1]}`, 0),
  };
};

/** @TODO refactor this component to remove recursion. */
const capturePhone = (reconciledPhone: string | null): any => {
  const validationMessage = reconciledPhone !== null ? '\n10 digits without spaces or dashes.' : '';
  reconciledPhone = prompt(
    `Whoops, we need a valid phone number in order to check out! ${validationMessage}`,
    reconciledPhone || '',
  );
  if (reconciledPhone) {
    reconciledPhone = reconciledPhone.replace(/\D/g, '');
  }
  if (reconciledPhone !== null && invalidPhone(reconciledPhone)) {
    return capturePhone(reconciledPhone);
  }
  return reconciledPhone;
};

/**
 * Helper function to assemble payment details without customer details
 * @param payment
 * @param storedValue
 * @param checkoutBasket
 * @returns PaymentDetails[]
 */
export const assemblePaymentWithoutCustomer = (
  payment: PaymentDetails | null,
  storedValue: IStoredValue | null,
  checkoutBasket: CheckoutBasket,
) => {
  return assemblePayment(
    payment,
    storedValue,
    {
      first_name: '',
      last_name: '',
      email: '',
      contact_number: '',
    },
    checkoutBasket,
  );
};

export const assemblePayment = (
  payment: PaymentDetails | null,
  storedValue: IStoredValue | null,
  customerDetails: CustomerDetails,
  checkoutBasket: CheckoutBasket,
): PaymentDetails[] => {
  if (!payment && !storedValue && checkoutBasket.total > 0) {
    throw new Error("Oops, please ensure you've added a payment method.");
  }

  const assembledPayment: PaymentDetails[] = [];
  const { paymentMethodAmount, storedValueAmount } = assignPaymentAmounts(
    checkoutBasket.total,
    storedValue?.amount_authorized ?? 0,
  );
  const paymentMethodTip = checkoutBasket.tip;

  // If a positive stored value has been applied to an order
  if (storedValue?.amount_authorized && storedValueAmount > 0) {
    const storedValuePayload = getStoredValuePaymentType(
      storedValue,
      storedValueAmount,
      customerDetails,
    );
    assembledPayment.push(storedValuePayload);
  }

  // If there is no remaining balance on the order,
  // do not attempt to assemble the payment method object
  if (paymentMethodAmount === 0) {
    return assembledPayment;
  }

  if (payment) {
    const paymentMethod: PaymentDetails = {
      ...payment,
      amount: paymentMethodAmount,
      tip: paymentMethodTip,
      customer: customerDetails,
    };

    // Push primary payment method
    assembledPayment.push(paymentMethod);
  }

  return assembledPayment;
};

const getStoredValuePaymentType = (
  storedValue: IStoredValue,
  storedValueAmount: number,
  customerDetails: CustomerDetails,
): PaymentDetails => {
  // Assemble stored value reqs
  const storedValueRequirements: StoredValueRequirement[] = [
    {
      name: 'number',
      value: storedValue?.card_number,
    },
  ];

  return {
    type: PAYMENT_TYPES.STORED_VALUE,
    amount: storedValueAmount,
    stored_value_requirements: storedValueRequirements,
    customer: customerDetails,
  };
};

const getUserDataFromState = (state: RootState) => {
  const me = state.app.me.data;
  const guestUser = state.app.auth.guestUser;
  return guestUser ?? (me?.id ? me : null);
};

export const assembleDeliveryObjectFromState = (
  state: RootState,
  instructions?: string,
  staleId?: number,
): IConveyanceDeliveryAddress | null => {
  const userData = getUserDataFromState(state);
  const basketFulfillment: IBasketFulfillment = state.app.basket?.fulfillment;

  /**
   * TEAM-14699: read the address from the same two places, in the same order, as
   * `selectConveyance` (redux/conveyanceMode/reducer.ts) — `basket.fulfillment?.address ??
   * conveyance.address`.
   *
   * `setDeliveryAddressSaga` only writes into `basket.fulfillment` when session storage
   * holds a basket entry for the current location; otherwise the address lands in the
   * conveyance slice alone. Reading only `basket.fulfillment` here made the submit path
   * behave as though the diner had entered no address at all — after a mid-session
   * location change or a cleared session — while the interface still showed one.
   */
  const address = basketFulfillment?.address ?? state.app.conveyanceMode?.address;

  if (!address) {
    return null;
  }

  /** @TODO reconcile the IConveyanceDeliveryAddress type. */
  const addressObj: IConveyanceDeliveryAddress = {
    id: address.id ?? undefined,
    stale_id: staleId || address.stale_id,
    // @ts-expect-error
    day_wanted: address.day_wanted,
    // @ts-expect-error
    time_wanted: address.time_wanted,
    city: address.city,
    // @ts-expect-error
    state: address.state,
    street_address: address.street_address,
    street_address_2: address.street_address_2 || null,
    zip_code: address.zip_code,
    phone_number:
      userData?.phone == null ? DEFAULT_GUEST_USER.phone : sanitizePhoneNumber(userData.phone),
    special_instructions: instructions || address.special_instructions || '',
    hand_off_type: address.hand_off_type ?? DELIVERY_HANDOFF_TYPE_DEFAULT,
    default: address.default,
  };

  if (addressObj.stale_id) {
    delete addressObj.id;
  }

  return addressObj;
};

/**
 * TEAM-15341: thrown by `assembleOrderDataFromState` when there is no checkout basket at all.
 *
 * Used to fall into the conveyance-mismatch throw (`SWAP[undefined]` never equals the diner's
 * handoff), so a submit against a torn-down basket — almost always a late wallet `token` after
 * the order had already gone through — was reported as a handoff problem and grouped with real
 * mismatches in COO-WEBAPP-3XN. Its own error name gives it its own Sentry issue.
 */
export class MissingCheckoutBasketError extends Error {
  constructor() {
    super("Oops, we couldn't find your cart. Please refresh this page and try again.");
    this.name = 'MissingCheckoutBasketError';
  }
}

/**
 * Whether the checkout basket's saved handoff matches the diner's handoff choice.
 *
 * `no_basket` is its own answer, not a mismatch: there is nothing to re-save a handoff onto.
 */
export const getConveyanceCheck = (state: RootState): 'match' | 'mismatch' | 'no_basket' => {
  const checkoutBasket = state.app.basket.checkoutBasket;
  if (!checkoutBasket) return 'no_basket';
  const basketConveyance =
    API_FOR_CONVEYANCE_TYPES_SWAP[
      checkoutBasket.conveyance_type?.type as keyof typeof API_FOR_CONVEYANCE_TYPES_SWAP
    ];
  return state.app.conveyanceMode?.type === basketConveyance ? 'match' : 'mismatch';
};

export const assembleOrderDataFromState = (state: RootState): SubmitBasketData => {
  const paymentTypeState = state.app.payment.paymentType;
  const storedValueState = state.app.payment.storedValue;
  const checkoutBasket = state.app.basket.checkoutBasket;
  const userData: LoyaltyUser | IUserInfo | null = getUserDataFromState(state);

  // TEAM-14996: don't submit a masked-placeholder email (e.g. ****@gmail.com) to the POS.
  // This is the all-sources belt behind the guest-form guard — it covers a logged-in profile
  // email and a digital-wallet payerEmail too, since both flow through `userData.email` here.
  // Toast rejects any '*' address with 400 / code 10025, so this surfaces a clear checkout
  // error (via orderSubmissionTriggerSaga's catch) instead of a cryptic POS rejection.
  if (isMaskedEmail(userData?.email)) {
    throw new Error(
      'There was a problem with the email on your order. Please check your email address and try again.',
    );
  }

  // Don't allow basket to be submitted without a phone number
  let reconciledPhone = '';
  if (invalidPhone(userData?.phone)) {
    reconciledPhone = capturePhone(userData?.phone ? userData.phone : null);
    if (!reconciledPhone) {
      throw new Error('Oops, please provide your phone number.');
    }
  }

  // Don't allow basket submission without a basket, or with a conveyance mismatch. Kept as the
  // last line of defence even though `orderSubmissionTriggerSaga` now re-saves a real mismatch
  // first (TEAM-15341): reaching here still mismatched means that re-save did not run.
  const conveyanceCheck = getConveyanceCheck(state);
  if (conveyanceCheck === 'no_basket') {
    throw new MissingCheckoutBasketError();
  }
  if (conveyanceCheck === 'mismatch') {
    throw new Error(
      'Oops, there was an error setting your handoff mode. Please refresh this page and try again.',
    );
  }

  /** @TODO reconcile the CustomerDetails type. */
  const customerDetails: CustomerDetails = {
    first_name: userData?.first_name ?? '',
    last_name: userData?.last_name ?? '',
    email: userData?.email ?? '',
    contact_number: sanitizePhoneNumber(reconciledPhone || userData?.phone),
  };
  const orderData = {
    payment: assemblePayment(paymentTypeState, storedValueState, customerDetails, checkoutBasket),
    customer: {
      ...customerDetails,
      opt_in: userData?.opt_in,
      email_receipt: false, // TODO make config if necessary in the future
    },
    allergies: [],
  };
  // @ts-expect-error selectedDigitalWallet  does not exist on type PaymentDetails
  return orderData;
};

export const assignPaymentAmounts = (
  checkoutTotal: number,
  storedValueAmountAuthorized: number,
) => {
  // Payment type covers the current checkout total by default
  let paymentMethodAmount = checkoutTotal;
  let storedValueAmount = 0;

  // If we have a gift card
  if (storedValueAmountAuthorized > 0) {
    const checkoutTotalSansGiftCard = checkoutTotal - storedValueAmountAuthorized;
    // If there is remaining checkout total after subtracting the gift card,
    // - calculate the remaining payment type amount
    // - assign the storedValueBalance to the storedValueAmount for checkout
    if (checkoutTotalSansGiftCard > 0) {
      paymentMethodAmount = checkoutTotalSansGiftCard;
      storedValueAmount = storedValueAmountAuthorized;
    }

    // If the gift card balance exceeds the cost of checkout
    // - there is no amount obligation on the payment type
    // - set the gift card remaining and the gift card amount
    if (checkoutTotalSansGiftCard <= 0) {
      paymentMethodAmount = 0;
      storedValueAmount = storedValueAmountAuthorized;
    }
  }

  return {
    paymentMethodAmount,
    storedValueAmount,
  };
};

export const assignPaymentTips = (tipAmount: number, storedValueBalance?: number) => {
  // Default tip assignments to 0
  let paymentMethodTip = 0;
  let storedValueTip = 0;

  // If stored value exists assign the tip to the stored value
  if (storedValueBalance) {
    storedValueTip = tipAmount;

    // If the stored value balance cannot cover the tip, assign the remander to the payment method
    if (storedValueBalance < tipAmount) {
      storedValueTip = storedValueBalance;
      paymentMethodTip = tipAmount - storedValueBalance;
    }
  }

  return {
    paymentMethodTip,
    storedValueTip,
  };
};

/**
 * Given a basket initialization error determine
 * the means by which it should be handled
 */
export const getInitializationErrorType = (message?: string) => {
  const initErrorKey = Object.keys(INITIALIZATION_ERRORS).find((key) => message?.includes(key));
  return initErrorKey ? INITIALIZATION_ERRORS[initErrorKey] : InitializationErrorTypes.UNHANDLED;
};

/**
 * Process a PCI Proxy change event and extract some non-sensitive card information
 * mainly the card type and the expiration date
 * see: https://docs.pci-proxy.com/collect-and-store-cards/capture-iframes/events
 *
 * @param data PCI Proxy iFrame change event payload
 * @param current state of payment data (mainly expiry)
 * @returns updated state
 */
export const handlePciProxyIFrameChangeEvent = (
  data: IFrameChangeEvent,
  { expiry }: { expiry: string },
) => {
  // currently we only handle the autocomplete event and only for
  if (data.event.type === 'autocomplete') {
    // the expire date is stored as a short date (e.g. 12/25)
    // so splitting at the slash gives month and year
    const date = (expiry || '/').split('/');
    const month = date[0]?.trim() || '';
    const year = date[1]?.trim() || '';

    switch (data.event.field) {
      case 'expiryMonth':
        expiry = `${data.event.value}/${year}`;
        break;
      case 'expiryYear':
        // grab only the last two digits for the year
        expiry = `${month}/${data.event.value?.slice(-2)}`;
        break;
      default:
        break;
    }
  }

  const cardType = data.fields.cardNumber.paymentMethod;

  return {
    cardType,
    expiry,
  };
};
